import { LayoutGrid, Sparkles, Settings, Plus, Sun, Moon, Mail, LogOut, Users, Linkedin, type LucideIcon } from 'lucide-react';
import { useMailStore } from '../../store/mail';
import { useUiStore, type AppView } from '../../store/ui';
import { useAuth } from '../../store/auth';

// Voci di navigazione principali (coerenti con la sidebar):
// Cruscotto | Posta | Contatti | LinkedIn. Le sottovoci vivono nella sidebar.
// `match` elenca le viste per cui la voce risulta attiva (es. Posta copre anche
// Ricerca/Cartelle/Feed, LinkedIn copre anche i vecchi Salvati).
const NAV: { view: AppView; label: string; icon: LucideIcon; match: AppView[] }[] = [
  { view: 'dashboard', label: 'Cruscotto', icon: LayoutGrid, match: ['dashboard', 'kpi'] },
  { view: 'list', label: 'Posta', icon: Mail, match: ['list', 'search', 'folders', 'feed'] },
  { view: 'contacts', label: 'Contatti', icon: Users, match: ['contacts'] },
  { view: 'linkedin', label: 'LinkedIn', icon: Linkedin, match: ['linkedin', 'saved'] },
];

export function TopBar() {
  const setAddAccountOpen = useMailStore((s) => s.setAddAccountOpen);
  const openSettings = useUiStore((s) => s.openSettings);
  const currentView = useUiStore((s) => s.currentView);
  const setCurrentView = useUiStore((s) => s.setCurrentView);
  const theme = useUiStore((s) => s.theme);
  const toggleTheme = useUiStore((s) => s.toggleTheme);
  const signOut = useAuth((s) => s.signOut);

  return (
    <header className="flex h-12 items-center justify-between border-b border-zinc-800/80 bg-zinc-950/50 px-4 backdrop-blur">
      <div className="flex items-center gap-3">
        <div className="flex h-7 w-7 items-center justify-center rounded-md bg-gradient-to-br from-accent-500 to-cyan-700 shadow-lg shadow-accent-500/20">
          <Sparkles size={14} className="text-white" strokeWidth={2.5} />
        </div>
        <h1 className="text-sm font-semibold tracking-tight">WebMail AI</h1>
        <span className="rounded bg-zinc-800/80 px-1.5 py-0.5 text-[10px] font-mono uppercase text-zinc-400">
          beta
        </span>
      </div>

      {/* Navigazione a viste */}
      <nav className="flex items-center gap-1 rounded-lg border border-zinc-800/80 bg-zinc-900/40 p-0.5">
        {NAV.map(({ view, label, icon: Icon, match }) => {
          const active = match.includes(currentView);
          return (
            <button
              key={view}
              onClick={() => setCurrentView(view)}
              className={
                'flex items-center gap-1.5 rounded-md px-3 py-1 text-xs font-medium transition ' +
                (active
                  ? 'bg-accent-500/15 text-accent-200'
                  : 'text-zinc-400 hover:bg-zinc-800/60 hover:text-zinc-100')
              }
            >
              <Icon size={13} className={active ? 'text-accent-400' : ''} />
              {label}
            </button>
          );
        })}
      </nav>

      <div className="flex items-center gap-2">
        <button
          onClick={toggleTheme}
          title={theme === 'dark' ? 'Passa al tema chiaro' : 'Passa al tema scuro'}
          className="rounded-md p-1.5 text-zinc-400 transition hover:bg-zinc-800/60 hover:text-zinc-100"
        >
          {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
        </button>
        <button
          onClick={() => setAddAccountOpen(true)}
          title="Aggiungi account"
          className="rounded-md p-1.5 text-zinc-400 transition hover:bg-zinc-800/60 hover:text-zinc-100"
        >
          <Plus size={16} />
        </button>
        <button
          onClick={() => openSettings()}
          title="Impostazioni"
          className="rounded-md p-1.5 text-zinc-400 transition hover:bg-zinc-800/60 hover:text-zinc-100"
        >
          <Settings size={16} />
        </button>
        {/* Logout: esce dalla sessione (disponibile in tutte le viste) */}
        <button
          onClick={() => signOut()}
          title="Esci"
          className="rounded-md p-1.5 text-zinc-400 transition hover:bg-zinc-800/60 hover:text-rose-300"
        >
          <LogOut size={16} />
        </button>
      </div>
    </header>
  );
}
